/*
 * Turning a membership id into a person — change order 3 §6.
 *
 * The API returns membership ids and never names, deliberately: resolving a name inside `clients`
 * or `pipeline` would mean reading `platform.user_account`, which those modules do not own. So the
 * join happens here, in the browser, where the team list from `access.member.list` is already
 * loaded beside whatever referenced the id.
 *
 * This file exists because that join was written ten times. `client-detail`, `interviews`,
 * `standup`, `job-commitments` and six others each had their own `nameFor` or `memberById`, and
 * every one of them printed the email address — which is what §6 is complaining about when it says
 * the screen shows a role word where a person should be. Ten copies is also ten places to forget
 * the fallback now that `full_name` can be null.
 */

/** The shape `access.member.list` returns, narrowed to what naming a person needs. */
export interface DirectoryMember {
  readonly id: string;
  readonly email: string;
  readonly full_name: string | null;
  readonly role_key: string;
  readonly status: string;
}

/**
 * What to call somebody.
 *
 * The email address is the fallback, not an error state: `full_name` is null for everybody invited
 * before migration 028 and for anybody invited without one. Falling back to the address means this
 * change is invisible on a tenant that has not filled the field in, rather than a screen full of
 * blanks — and the address is what every one of these screens displayed before today.
 *
 * `|| ` rather than `??` on purpose: the column is checked non-blank in the database, but a value
 * arriving over HTTP is whatever the wire said, and a name of `"   "` should read as absent rather
 * than as a person with no name.
 */
export function personName(member: DirectoryMember | undefined, fallback: string): string {
  const named = member?.full_name?.trim();
  if (named) {
    return named;
  }
  return member?.email ?? fallback;
}

/** The member behind an id, or undefined when the team list has not loaded or they have gone. */
export function memberById(
  members: readonly DirectoryMember[],
  membershipId: string,
): DirectoryMember | undefined {
  return members.find((member) => member.id === membershipId);
}
